import { useEffect, useState, useRef, useMemo } from 'react';

import { YoutubePlayer, VimeoPlayer } from '$utilities/classes';
import { useLazyEffect } from './lifecycle';

/* ========================================================================= */
/* History */
/* ========================================================================= */

/**
 * @hook useLocation
 * @return {object}
 *
 * Once rendered in the browser, provides the location object to the component.
 */
export const useLocation = () => {
    const [location, setLocation] = useState(null);

    useEffect(() => {
        const normalizedUrl = new URL(window.location.href);
        setLocation(normalizedUrl);
    }, []);

    return location;
};

/* ========================================================================= */
/* Storage */
/* ========================================================================= */

/**
 * @hook useLocalStorage
 * @param {string} namespace
 * @param {any} defaultValue
 * @param {object} config
 *     @property {boolean} shoultParse (optional)
 * @return {array}
 *
 * Once rendered in the browser, provides an interface for the local storage.
 */
export const useLocalStorage = (namespace, defaultValue, { shoultParse = true } = {}) => {
    const [localStorageDrawer, setLocalStorageDrawer] = useState(defaultValue);

    useEffect(() => {
        const data = localStorage.getItem(namespace);
        const preparedData = data ? (shoultParse ? JSON.parse(data) : data) : defaultValue;
        setLocalStorageDrawer(preparedData);
    }, [namespace]);

    const updateLocalStorageDrawer = data => {
        const preparedData = shoultParse ? JSON.stringify(data) : data;
        localStorage.setItem(namespace, preparedData);
        setLocalStorageDrawer(data);
    };

    return [localStorageDrawer, updateLocalStorageDrawer];
};

/* ========================================================================= */
/* Players */
/* ========================================================================= */

/**
 * @hook usePlayer
 *
 * Unified hook managing all the players (YouTube, Vimeo,…) in the same ecosystem.
 * 1. Once the element reference is provided, sets the player element (iframe).
 * 2. Once the player element is available, initializes the player.
 * 2.1. Determines which platform player should be used.
 * 2.2. Based on the determined platform, uses the equivalent interface (if available).
 * 2.3. If the video has been stopped from the player, propagates to the state.
 * 3. Propagates the play to the interface and mutates the state.
 * 4. Propagates the pause to the interface and mutates the state.
 */
export const usePlayer = ({ isAutoplayed = false } = {}) => {
    const [playerElement, setPlayerElement] = useState(null);
    const [player, setPlayer] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPausedAt, setIsPausedAt] = useState(null);
    const nodeRef = useRef(null);

    /* [1] */
    const play = () => {
        player?.play();
        setIsPausedAt(null);
        setIsPlaying(true);
    };

    const playAt = time => {
        player?.playAt(time);
        setIsPausedAt(null);
        setIsPlaying(true);
    };

    /* [2] */
    const pause = () => {
        player?.pause();
        setIsPlaying(false);
    };

    /* [3] */
    const ref = node => {
        try {
            if (!(node && node !== nodeRef.current)) {
                return;
            }

            nodeRef.current = node;

            const _playerElement = node.matches('iframe')
                ? node
                : node.querySelector('iframe');

            setIsPlaying(false);
            setPlayerElement(_playerElement);
        } catch (err) {
            console.error(err);
        }
    };

    /* [4] */
    useEffect(() => {
        if (!playerElement) {
            return;
        }

        /* [4.1] */
        const platform = playerElement.src.match(/(youtube|vimeo)/) ? playerElement.src.match(/(youtube|vimeo)/)[0] : null;

        /* [4.2] */
        if (platform === 'youtube') {
            const _player = new YoutubePlayer(playerElement);
            _player.onPlay = () => {
                setIsPlaying(true);
            };
            _player.onPause = (time) => {
                setIsPausedAt(time);
                setIsPlaying(false);
            };
            _player.onEnd = () => {
                setIsPlaying(false);
                setIsPausedAt(null);
            }; /* [4.3] */
            setPlayer(_player);
        } else if (platform === 'vimeo') {
            const _player = new VimeoPlayer(playerElement);
            _player.onPlay = () => {
                setIsPlaying(true);
            };
            _player.onPause = (time) => {
                setIsPausedAt(time);
                setIsPlaying(false);
            };
            _player.onEnd = () => {
                setIsPausedAt(null);
                setIsPlaying(false);
            }; /* [4.3] */
            setPlayer(_player);
        }
    }, [playerElement]);

    useLazyEffect(() => {
        if (!(isAutoplayed && player)) {
            return;
        }

        if (isPausedAt) {
            playAt(isPausedAt);
        }
        else {
            play();
        }
    }, [isAutoplayed, player]);

    return useMemo(() => ({
        ref,
        player,
        play,
        playAt,
        pause,
        isPlaying,
        isPausedAt
    }), [
        player,
        isPlaying,
        isPausedAt
    ]);
};
